import type { UserTemplateData } from "@/shared/types";
import { formatPhoneNumber } from "./phone-formatter";
import { GENERAL_DATA } from "@/shared/constants/mock-data";

/**
 * Normaliza dados brutos do perfil do usuário para o formato `UserTemplateData`,
 * garantindo que todo placeholder de template tenha um valor resolvido.
 *
 * Cada campo tem uma fonte de origem:
 * - **Microsoft Graph**  identidade e cargo (`display_name`, `first_name`,
 *   `last_name`, `email`, `job_title`, `mobile_phone`, `photo`).
 * - **`GENERAL_DATA`**  dados institucionais iguais para todo mundo.
 *   Endereço e WhatsApp sempre vencem o que vier em `userInfo`; os telefones
 *   fixos são apenas **fallback**, porque o usuário pode sobrescrevê-los quando
 *   o administrador libera a edição e esta função roda de novo em todo caminho
 *   de renderização (preview, aplicar, copiar e inserção automática)
 *   forçar o institucional aqui apagava a edição na hora de renderizar.
 *   O Graph não preenche nenhum desses campos (ver `graph-user-mapper.ts`).
 * - **usuário**  `certifications` e `ramal`, mais os campos de contato e
 *   endereço quando o administrador libera a edição (ver `ALL_OVERRIDE_FIELDS`).
 *   Esse override é aplicado por cima do resultado desta função, em
 *   `user-data-cache.ts`  não dentro dela.
 * - **HTML da assinatura**  links de redes sociais e site, que ficam fixos no
 *   template cadastrado na plataforma.
 *
 * Regras de derivação:
 * - `name` ↔ `display_name`, `job` ↔ `job_title`, `photo` ↔ `photo_base64` (bidirecionais)
 * - `first_name` / `last_name` extraídos de `name` quando não fornecidos
 * - `certifications` é digitado como lista separada por `;` (ex: "A; B; C;") e
 *   mantido intacto nesse formato  é o valor usado no campo de edição.
 *   `certifications_display` é derivado dele separado por `" | "` e é o único
 *   usado no HTML do template.
 * - Todo campo ausente vira `""`, para que os blocos `{{#campo}}` do template
 *   simplesmente não rendam em vez de deixar buraco no layout.
 *
 * @param userInfo - Dados brutos do usuário (do cache local, roamingSettings ou Graph API).
 * @returns Objeto `UserTemplateData` normalizado com todos os campos preenchidos.
 *
 * @example
 * ```typescript
 * const normalized = normalizeUserData({ name: "João Silva", email: "j@e.com" });
 * // { display_name: "João Silva", first_name: "João", last_name: "Silva", ... }
 * ```
 */
export function normalizeUserData(userInfo: any): UserTemplateData {
  const nameParts = (userInfo.name || userInfo.display_name || "")
    .trim()
    .split(" ");
  const firstName =
    userInfo.first_name || nameParts[0] || "";
  const lastName =
    userInfo.last_name ||
    (nameParts.length > 1 ? nameParts.slice(1).join(" ") : "");

  const certificationsDisplay = userInfo.certifications
    ? String(userInfo.certifications)
        .split(";")
        .map((c: string) => c.trim())
        .filter(Boolean)
        .join(" | ")
    : "";

  return {
    display_name: userInfo.display_name || userInfo.name || "",
    first_name: firstName,
    last_name: lastName,
    email: userInfo.email || "",
    job_title: userInfo.job_title || userInfo.job || "",
    office_phone_1: formatPhoneNumber(userInfo.office_phone_1 || GENERAL_DATA.office_phone_1),
    office_phone_2: formatPhoneNumber(userInfo.office_phone_2 || GENERAL_DATA.office_phone_2),
    mobile_phone: formatPhoneNumber(userInfo.mobile_phone || ""),
    street_address: GENERAL_DATA.street_address,
    city: GENERAL_DATA.city,
    state: GENERAL_DATA.state,
    zip_code: GENERAL_DATA.zip_code,
    country: GENERAL_DATA.country,
    photo_base64: userInfo.photo_base64 || userInfo.photo || "",
    name: userInfo.name || userInfo.display_name || "",
    job: userInfo.job || userInfo.job_title || "",
    photo: userInfo.photo || userInfo.photo_base64 || "",
    ramal: userInfo.ramal || "",
    whatsapp: GENERAL_DATA.whatsapp,
    facebook_url: userInfo.facebook_url || "",
    twitter_url: userInfo.twitter_url || "",
    linkedin_url: userInfo.linkedin_url || "",
    instagram_url: userInfo.instagram_url || "",
    website_url: userInfo.website_url || "",
    certifications: userInfo.certifications || "",
    certifications_display: certificationsDisplay,
  };
}
